import { useEffect, useState } from "react"
import type { ProductProfileId } from "@/lib/api/tenant-settings"

const PRODUCT_PROFILE_OVERRIDE_KEY = "eca-product-profile-override"
const PRODUCT_PROFILE_OVERRIDE_EVENT = "eca:product-profile-override"

export function resolveProductProfileOverrideQuery(
  rawProfile: string | null
): ProductProfileId | null {
  if (rawProfile === "lite" || rawProfile === "eca_lite") {
    return "eca_lite"
  }
  return null
}

export function getSessionProductProfileOverride(): ProductProfileId | null {
  if (typeof window === "undefined") return null
  const stored = sessionStorage.getItem(PRODUCT_PROFILE_OVERRIDE_KEY)
  return stored === "eca_lite" ? "eca_lite" : null
}

function emitProductProfileOverrideChanged() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(PRODUCT_PROFILE_OVERRIDE_EVENT))
}

export function setSessionProductProfileOverride(
  profileId: ProductProfileId | null
) {
  if (typeof window === "undefined") return

  if (profileId) {
    sessionStorage.setItem(PRODUCT_PROFILE_OVERRIDE_KEY, profileId)
  } else {
    sessionStorage.removeItem(PRODUCT_PROFILE_OVERRIDE_KEY)
  }

  emitProductProfileOverrideChanged()
}

export function clearSessionProductProfileOverride() {
  setSessionProductProfileOverride(null)
}

export function shouldUseLitePublicEntry(search?: string | null): boolean {
  const queryOverride = resolveProductProfileOverrideQuery(
    new URLSearchParams(search || "").get("profile")
  )

  return (
    queryOverride === "eca_lite" ||
    getSessionProductProfileOverride() === "eca_lite"
  )
}

export function getPublicLoginRoute(search?: string | null): string {
  return shouldUseLitePublicEntry(search) ? "/lite/login" : "/login"
}

export function useSessionProductProfileOverride() {
  const [override, setOverride] = useState<ProductProfileId | null>(() =>
    getSessionProductProfileOverride()
  )

  useEffect(() => {
    const syncOverride = () => {
      setOverride(getSessionProductProfileOverride())
    }

    window.addEventListener(PRODUCT_PROFILE_OVERRIDE_EVENT, syncOverride)
    window.addEventListener("storage", syncOverride)

    return () => {
      window.removeEventListener(PRODUCT_PROFILE_OVERRIDE_EVENT, syncOverride)
      window.removeEventListener("storage", syncOverride)
    }
  }, [])

  return override
}

